import {
  doublePrecision,
  pgTable,
  primaryKey,
  text,
  uuid,
  integer,
  foreignKey,
  unique,
} from "drizzle-orm/pg-core";
import { id, sortIndex } from "../../utils/sql";
import { nutrient } from "../nutrient/nutrient.sql";
import { relations } from "drizzle-orm";
import { food, foodMeta, foodServing } from "../food/food.sql";
import { recipeVariation } from "../recipe-variation/recipe-variation.sql";

/**
 * Recipe persistence schema.
 *
 * Invariants:
 * - Recipe, ingredients, steps, servings, and nutrients are version-scoped.
 * - Ingredient/step uniqueness per recipe version is enforced by SQL constraints.
 *
 * @see `packages/core/docs/database.md`
 */
export const recipe = pgTable(
  "recipe",
  {
    id: uuid("id").notNull(),
    version: integer("version").notNull(),
    description: text("description"),
    prepTimeMinutes: integer("prep_time_minutes"),
    cookTimeMinutes: integer("cook_time_minutes"),
  },
  (t) => [
    primaryKey({ name: "recipe_pk", columns: [t.id, t.version] }),
    foreignKey({
      name: "recipe_food_fk",
      columns: [t.id, t.version],
      foreignColumns: [food.id, food.version],
    }),
  ],
);

export const recipeRelations = relations(recipe, ({ one, many }) => ({
  base: one(food, {
    fields: [recipe.id, recipe.version],
    references: [food.id, food.version],
  }),
  meta: one(foodMeta, {
    fields: [recipe.id],
    references: [foodMeta.id],
  }),
  metaIfCurrent: one(foodMeta, {
    fields: [recipe.id, recipe.version],
    references: [foodMeta.id, foodMeta.currentVersion],
  }),
  ingredients: many(recipeIngredient),
  steps: many(recipeStep),
  serving: one(recipeServing),
  variations: many(recipeVariation),
}));

export const recipeIngredient = pgTable(
  "recipe_ingredient",
  {
    ...id,
    ...sortIndex,
    recipeId: uuid("recipe_id").notNull(),
    recipeVersion: integer("recipe_version").notNull(),
    foodServingId: uuid("food_serving_id")
      .notNull()
      .references(() => foodServing.id),
    amount: doublePrecision("amount").notNull(),
  },
  (t) => [
    unique("recipe_ingredient_unique_food_serving").on(
      t.recipeId,
      t.recipeVersion,
      t.foodServingId,
    ),
    unique("recipe_ingredient_unique_sort_index").on(t.recipeId, t.recipeVersion, t.sortIndex),
    foreignKey({
      name: "recipe_ingredient_recipe_fk",
      columns: [t.recipeId, t.recipeVersion],
      foreignColumns: [recipe.id, recipe.version],
    }),
  ],
);

export const recipeIngredientRelations = relations(recipeIngredient, ({ one }) => ({
  recipe: one(recipe, {
    fields: [recipeIngredient.recipeId, recipeIngredient.recipeVersion],
    references: [recipe.id, recipe.version],
  }),
  serving: one(foodServing, {
    fields: [recipeIngredient.foodServingId],
    references: [foodServing.id],
  }),
}));

export const recipeNutrient = pgTable(
  "recipe_nutrient",
  {
    recipeId: uuid("recipe_id").notNull(),
    recipeVersion: integer("recipe_version").notNull(),
    nutrientId: uuid("nutrient_id")
      .references(() => nutrient.id)
      .notNull(),
    amount: doublePrecision("amount").notNull(),
  },
  (t) => [
    primaryKey({
      name: "recipe_nutrient_pk",
      columns: [t.recipeId, t.recipeVersion, t.nutrientId],
    }),
    foreignKey({
      name: "recipe_nutrient_recipe_fk",
      columns: [t.recipeId, t.recipeVersion],
      foreignColumns: [recipe.id, recipe.version],
    }),
  ],
);

export const recipeServing = pgTable(
  "recipe_serving",
  {
    ...id,
    recipeId: uuid("recipe_id").notNull(),
    recipeVersion: integer("recipe_version").notNull(),
    perRecipe: doublePrecision("per_recipe").notNull(),
  },
  (t) => [
    foreignKey({
      name: "recipe_serving_recipe_fk",
      columns: [t.recipeId, t.recipeVersion],
      foreignColumns: [recipe.id, recipe.version],
    }),
    foreignKey({
      name: "recipe_serving_food_serving_fk",
      columns: [t.recipeId, t.id],
      foreignColumns: [foodServing.foodId, foodServing.id],
    }),
    unique("recipe_serving_unique").on(t.recipeId, t.recipeVersion),
  ],
);

export const recipeServingRelations = relations(recipeServing, ({ one }) => ({
  recipe: one(recipe, {
    fields: [recipeServing.recipeId, recipeServing.recipeVersion],
    references: [recipe.id, recipe.version],
  }),
  base: one(foodServing, {
    fields: [recipeServing.id],
    references: [foodServing.id],
  }),
}));

export const recipeStep = pgTable(
  "recipe_step",
  {
    ...id,
    ...sortIndex,
    recipeId: uuid("recipe_id").notNull(),
    recipeVersion: integer("recipe_version").notNull(),
    text: text("text").notNull(),
  },
  (t) => [
    unique("recipe_step_unique_sort_index").on(t.recipeId, t.recipeVersion, t.sortIndex),
    foreignKey({
      name: "recipe_step_recipe_fk",
      columns: [t.recipeId, t.recipeVersion],
      foreignColumns: [recipe.id, recipe.version],
    }),
  ],
);

export const recipeStepRelations = relations(recipeStep, ({ one }) => ({
  recipe: one(recipe, {
    fields: [recipeStep.recipeId, recipeStep.recipeVersion],
    references: [recipe.id, recipe.version],
  }),
}));
